import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FaPlus, FaSearch, FaTshirt, FaEye, FaEyeSlash, FaTrash, FaPencilAlt } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, StockBadge, EmptyState, Toast, useToast } from '../components/ui'
import Thumb from '../components/Thumb'
import DeleteProductDialog from '../components/DeleteProductDialog'
import { money, number } from '../lib/format'
import * as api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { SHOW_STOCK } from '../lib/features'

/**
 * The catalogue, as a working list.
 *
 * Filtering and sorting happen client-side. The catalogue is under a hundred
 * products, so shipping all of it once and filtering in memory is faster and far
 * more responsive than a round trip per keystroke; past a few thousand this would
 * need to move server-side.
 */

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Live' },
  { value: 'draft', label: 'Drafts' },
  { value: 'archived', label: 'Archived' },
]

const SORTS = {
  manual: { label: 'Shop order', compare: (a, b) => a.sort_order - b.sort_order },
  bestselling: { label: 'Best selling', compare: (a, b) => (b.units_sold ?? 0) - (a.units_sold ?? 0) },
  revenue: { label: 'Revenue', compare: (a, b) => (b.revenue ?? 0) - (a.revenue ?? 0) },
  name: { label: 'Name', compare: (a, b) => a.name.localeCompare(b.name) },
  priceHigh: { label: 'Price, high to low', compare: (a, b) => b.price - a.price },
  priceLow: { label: 'Price, low to high', compare: (a, b) => a.price - b.price },
}

const ProductsPage = () => {
  // Catalogue changes are owner/manager only (RLS: products writes need
  // is_manager). Staff see the catalogue without the controls.
  const { canManage } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [products, setProducts] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(searchParams.get('status') ?? 'all')
  const [category, setCategory] = useState(searchParams.get('category') ?? 'all')
  const [sort, setSort] = useState('manual')
  const { toast, notify, notifyError, dismiss } = useToast()
  const [removing, setRemoving] = useState(null)

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      setProducts(await api.getProducts())
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const categories = useMemo(
    () => [...new Set(products.map((p) => p.category).filter(Boolean))].sort(),
    [products],
  )

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()
    return products
      .filter((p) => {
        if (status !== 'all' && p.status !== status) return false
        if (category !== 'all' && p.category !== category) return false
        if (!query) return true
        return (
          p.name.toLowerCase().includes(query) ||
          (p.sku ?? '').toLowerCase().includes(query) ||
          (p.brand ?? '').toLowerCase().includes(query)
        )
      })
      .sort(SORTS[sort].compare)
  }, [products, search, status, category, sort])

  const toggleStatus = async (product) => {
    const next = product.status === 'active' ? 'draft' : 'active'
    try {
      await api.setProductStatus(product.id, next)
      notify(
        next === 'active'
          ? `${product.name} is now live on the shop.`
          : `${product.name} is hidden from the shop.`,
      )
      load()
    } catch (err) {
      notifyError(err)
    }
  }

  if (state.loading) return <Spinner label="Loading the catalogue…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load products.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader title="Products" subtitle={`${number(products.length)} products in the catalogue`}>
        {canManage && (
          <Link to="/products/new" className="btn-primary">
            <FaPlus className="text-[11px]" />
            Add a product
          </Link>
        )}
      </PageHeader>

      {!canManage && (
        <div className="mb-5">
          <Banner tone="info">
            You can look up products here. Adding, editing, pricing and hiding products is for the
            owner and managers{SHOW_STOCK ? ' — stock levels are changed from Inventory' : ''}.
          </Banner>
        </div>
      )}

      {/* Filters float above the grid rather than framing it — a grid of cards
          reads as a shop shelf, not a spreadsheet, and a heavy border around it
          would fight that. */}
      <div className="mb-5 flex flex-wrap items-center gap-2.5 rounded-2xl border border-hairline bg-surface p-3.5 shadow-card">
        <div className="relative min-w-[200px] flex-1">
          <FaSearch
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] text-ink-muted"
            aria-hidden="true"
          />
          <input
            className="input pl-9"
            placeholder="Search name, code or brand"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search products"
          />
        </div>

        <div className="flex rounded-lg border border-hairline p-0.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatus(f.value)}
              className={`rounded-md px-2.5 py-1.5 text-2xs font-medium transition-colors ${
                status === f.value ? 'bg-brand-navy text-white' : 'text-ink-secondary hover:text-ink'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <select
          className="input input-sm w-auto"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Filter by category"
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          className="input input-sm w-auto"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="Sort products"
        >
          {Object.entries(SORTS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {visible.length === 0 ? (
        <Card>
          <EmptyState
            icon={FaTshirt}
            title="No products match"
            action={
              <button
                onClick={() => {
                  setSearch('')
                  setStatus('all')
                  setCategory('all')
                }}
                className="btn-ghost"
              >
                Clear filters
              </button>
            }
          >
            Try a different search, or clear the filters.
          </EmptyState>
        </Card>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {visible.map((product) => (
            <div
              key={product.id}
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-hairline bg-surface shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-raised"
            >
              <Link
                to={`/products/${product.id}`}
                className="block aspect-square shrink-0 border-b border-hairline bg-plane"
                tabIndex={-1}
                aria-hidden="true"
              >
                <Thumb src={product.image} alt="" size="full" className="rounded-none border-0" />
              </Link>

              {product.status !== 'active' && (
                <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-status-warning shadow-sm">
                  {product.status}
                </span>
              )}

              {/* Edit, visibility and remove float in on hover rather than
                  sitting in the flow permanently — the card's job at rest is
                  to be looked at, not operated. */}
              <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 focus-within:opacity-100 group-hover:opacity-100">
                <Link
                  to={`/products/${product.id}`}
                  className="grid h-7 w-7 place-items-center rounded-full bg-white/95 text-ink-secondary shadow-sm hover:text-brand-navy"
                  title={canManage ? 'Edit this product' : 'View this product'}
                  aria-label={`${canManage ? 'Edit' : 'View'} ${product.name}`}
                >
                  <FaPencilAlt className="text-[11px]" />
                </Link>
                {canManage && (
                <>
                <button
                  onClick={() => toggleStatus(product)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-white/95 text-ink-secondary shadow-sm hover:text-brand-navy"
                  title={product.status === 'active' ? 'Hide from the shop' : 'Show on the shop'}
                  aria-label={
                    product.status === 'active'
                      ? `Hide ${product.name} from the shop`
                      : `Show ${product.name} on the shop`
                  }
                >
                  {product.status === 'active' ? <FaEye className="text-[11px]" /> : <FaEyeSlash className="text-[11px]" />}
                </button>
                <button
                  onClick={() => setRemoving(product)}
                  className="grid h-7 w-7 place-items-center rounded-full bg-white/95 text-brand-red shadow-sm hover:bg-brand-red hover:text-white"
                  title="Archive or delete"
                  aria-label={`Remove ${product.name}`}
                >
                  <FaTrash className="text-[11px]" />
                </button>
                </>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-2 p-3.5">
                <div className="min-w-0">
                  <Link
                    to={`/products/${product.id}`}
                    className="block truncate font-heading text-[13px] font-semibold text-ink hover:text-brand-red"
                  >
                    {product.name}
                  </Link>
                  <div className="mt-0.5 truncate text-2xs text-ink-muted">
                    {product.sku}
                    {product.brand ? ` · ${product.brand}` : ''}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <span className="text-[15px] font-semibold tabular-nums text-ink">{money(product.price)}</span>
                  {product.category && (
                    <span className="truncate rounded-full bg-plane px-2 py-0.5 text-2xs font-medium text-ink-secondary">
                      {product.category}
                    </span>
                  )}
                </div>

                {/* No inventory rows means stock is not being tracked, which is
                    different from "zero in stock" and must not be shown as a
                    warning. The whole line is hidden while stock is switched
                    off — see lib/features.js. */}
                {SHOW_STOCK &&
                  (product.stock_on_hand > 0 || product.in_stock === false ? (
                    <StockBadge available={product.stock_on_hand} />
                  ) : (
                    <span className="text-2xs text-ink-muted">Not tracked</span>
                  ))}

                <div className="mt-auto flex items-center justify-between gap-2 border-t border-hairline pt-2.5 text-2xs text-ink-muted">
                  <span>{number(product.units_sold)} sold</span>
                  <span className="tabular-nums">{money(product.revenue)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteProductDialog
        product={removing}
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onDone={(message) => { notify(message); load() }}
      />

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

export default ProductsPage
